import { useEffect, useState } from "react";
import { FormState, MealPlanFormProps } from "types/mealPlans";

import Button from "components/atoms/Button";

const MealPlanForm: React.FC<MealPlanFormProps> = ({
  onGenerateMealPlan,
  onGeneratePreview,
  isLoading,
}) => {
  // Get stored form data from localStorage or use defaults
  const getInitialState = (): FormState => {
    const savedForm = localStorage.getItem("mealPlanFormState");
    if (savedForm) {
      return JSON.parse(savedForm);
    }
    return {
      weight: "",
      height: "",
      age: "",
      sex: "",
      activityLevel: "",
      dietaryRestrictions: [],
      allergies: [],
      additionalInfo: "",
      days: 3,
    };
  };

  const [formState, setFormState] = useState<FormState>(getInitialState);

  // Save form state to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem("mealPlanFormState", JSON.stringify(formState));
  }, [formState]);

  // Handle input changes
  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value } = e.target;
    setFormState((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle checkbox changes
  const handleCheckboxChange = (
    category: "dietaryRestrictions" | "allergies",
    value: string
  ) => {
    setFormState((prev) => {
      const currentValues = [...prev[category]];
      const index = currentValues.indexOf(value);

      if (index === -1) {
        // Add value if not present
        return {
          ...prev,
          [category]: [...currentValues, value],
        };
      } else {
        // Remove value if already present
        currentValues.splice(index, 1);
        return {
          ...prev,
          [category]: currentValues,
        };
      }
    });
  };

  // Reset form
  const handleReset = () => {
    setFormState({
      weight: "",
      height: "",
      age: "",
      sex: "",
      activityLevel: "",
      dietaryRestrictions: [],
      allergies: [],
      additionalInfo: "",
      days: 3,
    });
  };

  return (
    <div className="p-4">
      <h3 className="text-xl font-semibold mb-4">Personal Details</h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Weight (kg)
          </label>
          <input
            type="number"
            name="weight"
            value={formState.weight}
            onChange={handleInputChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
            placeholder="Enter weight in kg"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Height (cm)
          </label>
          <input
            type="number"
            name="height"
            value={formState.height}
            onChange={handleInputChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
            placeholder="Enter height in cm"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Age
          </label>
          <input
            type="number"
            name="age"
            value={formState.age}
            onChange={handleInputChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
            placeholder="Enter your age"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Sex
          </label>
          <select
            name="sex"
            value={formState.sex}
            onChange={handleInputChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
          >
            <option value="">Select</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
            <option value="other">Other</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Activity Level
          </label>
          <select
            name="activityLevel"
            value={formState.activityLevel}
            onChange={handleInputChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
          >
            <option value="">Select</option>
            <option value="sedentary">Sedentary</option>
            <option value="light">Lightly Active</option>
            <option value="moderate">Moderately Active</option>
            <option value="active">Active</option>
            <option value="very_active">Very Active</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Days (1-7)
          </label>
          <input
            type="number"
            name="days"
            value={formState.days}
            onChange={handleInputChange}
            min="1"
            max="7"
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
          />
        </div>
      </div>

      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Dietary Restrictions
        </label>
        <div className="grid grid-cols-2 gap-2">
          {[
            "Vegetarian",
            "Vegan",
            "Gluten-Free",
            "Dairy-Free",
            "Keto",
            "Paleo",
            "Low-Carb",
            "Low-Fat",
          ].map((restriction) => (
            <div key={restriction} className="flex items-center">
              <input
                type="checkbox"
                id={`restriction-${restriction}`}
                checked={formState.dietaryRestrictions.includes(restriction)}
                onChange={() =>
                  handleCheckboxChange("dietaryRestrictions", restriction)
                }
                className="mr-2"
              />
              <label htmlFor={`restriction-${restriction}`} className="text-sm">
                {restriction}
              </label>
            </div>
          ))}
        </div>
      </div>

      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Allergies / Foods to Avoid
        </label>
        <div className="grid grid-cols-2 gap-2">
          {[
            "Nuts",
            "Shellfish",
            "Eggs",
            "Soy",
            "Wheat",
            "Fish",
            "Milk",
            "Peanuts",
          ].map((allergy) => (
            <div key={allergy} className="flex items-center">
              <input
                type="checkbox"
                id={`allergy-${allergy}`}
                checked={formState.allergies.includes(allergy)}
                onChange={() => handleCheckboxChange("allergies", allergy)}
                className="mr-2"
              />
              <label htmlFor={`allergy-${allergy}`} className="text-sm">
                {allergy}
              </label>
            </div>
          ))}
        </div>
      </div>

      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Additional Information
        </label>
        <textarea
          name="additionalInfo"
          value={formState.additionalInfo}
          onChange={handleInputChange}
          className="w-full px-3 py-2 border border-gray-300 rounded-md h-24"
          placeholder="Enter any additional information, preferences, or goals..."
        ></textarea>
      </div>

      <div className="flex flex-col sm:flex-row gap-4 mt-6">
        <Button
          text="Generate Meal Plan"
          onClick={() => onGenerateMealPlan(formState)}
          disabled={isLoading}
          isLoading={isLoading}
          fullWidth
        />
        <Button
          text="Generate Preview"
          onClick={() => onGeneratePreview(formState)}
          disabled={isLoading}
          isLoading={isLoading}
          outlined
          fullWidth
        />
        <Button
          text="Reset Form"
          onClick={handleReset}
          disabled={isLoading}
          isWarning
          outlined
          fullWidth
        />
      </div>
    </div>
  );
};

export default MealPlanForm;
